import type { Handler } from '../api/handlersApi';

const BASE_SUFFIX_RE = /(?:\.v\d+|:v?\d+|:latest)$/i;
const VERSION_RE = /^(.*?)(?:\.v|:v?)(\d+)$/i;

export function baseHandlerKey(handlerKeyOrRef: string): string {
  const k = String(handlerKeyOrRef ?? '').trim();
  if (!k) return '';
  return k.replace(BASE_SUFFIX_RE, '');
}

export function splitRef(ref: string): { base: string; tag: string } | null {
  const r = String(ref ?? '').trim();
  const idx = r.lastIndexOf(':');
  if (idx < 0) return null;
  const base = r.slice(0, idx).trim();
  const tag = r.slice(idx + 1).trim().toLowerCase();
  if (!base || !tag) return null;
  return { base, tag };
}

export function parseVersion(handlerKey: string): { base: string; version: number } | null {
  const k = String(handlerKey ?? '').trim();
  const m = k.match(VERSION_RE);
  if (!m) return null;
  const base = String(m[1] ?? '').trim();
  const v = Number(m[2]);
  if (!base || !Number.isFinite(v) || v <= 0) return null;
  return { base, version: v };
}

export function latestRefFor(handlerKey: string): string {
  const base = baseHandlerKey(handlerKey);
  return base ? `${base}:latest` : '';
}

export function resolveHandlerRef(handlers: Handler[], ref: string): Handler | null {
  const key = String(ref ?? '').trim();
  if (!key) return null;
  const exact = handlers.find((h) => h.handler_key === key);
  if (exact) return exact;

  const split = splitRef(key);
  if (!split) return null;

  const base = baseHandlerKey(split.base);
  if (!base) return null;

  if (split.tag === 'latest') {
    let best: { handler: Handler; version: number } | null = null;
    for (const h of handlers) {
      if (!h.is_active) continue;
      const pv = parseVersion(h.handler_key);
      if (!pv) continue;
      if (baseHandlerKey(pv.base) !== base) continue;
      if (!best || pv.version > best.version) best = { handler: h, version: pv.version };
    }
    return best?.handler ?? null;
  }

  const cleaned = split.tag.replace(/^v/i, '').trim();
  const v = Number(cleaned);
  if (!Number.isFinite(v) || v <= 0) return null;

  const candidates = [`${base}:v${v}`, `${base}:${v}`, `${base}.v${v}`];
  for (const cand of candidates) {
    const h = handlers.find((x) => x.handler_key === cand);
    if (h) return h;
  }
  return null;
}

export function buildHandlerOptionsForWorker(handlers: Handler[], workerId: number): Array<{ value: string; label: string }> {
  const filtered = handlers
    .filter((h) => h.worker_id === workerId)
    .slice()
    .sort((a, b) => a.handler_key.localeCompare(b.handler_key));

  // Group versioned handlers by base so we can add a :latest alias option per family.
  const byBase = new Map<string, Handler[]>();
  for (const h of filtered) {
    const base = baseHandlerKey(h.handler_key);
    if (!base) continue;
    const arr = byBase.get(base) ?? [];
    arr.push(h);
    byBase.set(base, arr);
  }

  const latestOpts: Array<{ value: string; label: string }> = Array.from(byBase.keys())
    .sort((a, b) => a.localeCompare(b))
    .map((base) => ({ value: `${base}:latest`, label: `${base}:latest` }));

  const pinnedOpts = filtered.map((h) => ({
    value: h.handler_key,
    label: `${h.handler_key}${h.is_active ? '' : ' (inactive)'}`,
  }));

  return [...latestOpts, ...pinnedOpts];
}
